<template>
  <div class="owe-fee-callable-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('oweFeeCallable.list.title')" :subtitle="$t('oweFeeCallable.page.subtitle')">
      <template #extra>
        <el-button type="primary" icon="el-icon-printer" @click="handleWritePrint">
          {{ $t('oweFeeCallable.list.register') }}
        </el-button>
        <el-button type="primary" icon="el-icon-plus" @click="handleAdd">
          {{ $t('oweFeeCallable.list.call') }}
        </el-button>
      </template>
    </ds-page-header>

    <div class="owe-fee-callable-layout">
      <!-- 左侧房屋树 -->
      <div class="ds-panel ds-panel--tree">
        <room-tree-div ref="roomTree" @selectRoom="handleSelectRoom"></room-tree-div>
      </div>

      <div class="owe-fee-callable-main">
        <!-- (2) SearchPanel -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="searchForm.ownerName" clearable class="w-full"
                :placeholder="$t('oweFeeCallable.search.ownerNamePlaceholder')" @keyup.enter.native="handleSearch" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="searchForm.feeName" clearable class="w-full"
                :placeholder="$t('oweFeeCallable.search.feeNamePlaceholder')" @keyup.enter.native="handleSearch" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="searchForm.callableWay" clearable class="w-full"
                :placeholder="$t('oweFeeCallable.search.callableWayPlaceholder')">
                <el-option :label="$t('common.all')" value="" />
                <el-option :label="$t('oweFeeCallable.page.wechat')" value="WECHAT" />
                <el-option :label="$t('oweFeeCallable.page.sms')" value="SMS" />
                <el-option :label="$t('oweFeeCallable.page.print')" value="PRINT" />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="searchForm.staffName" clearable class="w-full"
                :placeholder="$t('oweFeeCallable.search.staffNamePlaceholder')" @keyup.enter.native="handleSearch" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="searchForm.state" clearable class="w-full"
                :placeholder="$t('oweFeeCallable.search.statePlaceholder')">
                <el-option :label="$t('common.all')" value="" />
                <el-option :label="$t('oweFeeCallable.page.pending')" value="W" />
                <el-option :label="$t('oweFeeCallable.page.done')" value="C" />
                <el-option :label="$t('oweFeeCallable.page.failed')" value="F" />
                <el-option :label="$t('oweFeeCallable.page.doing')" value="D" />
              </el-select>
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">
              {{ $t('common.search') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="handleReset">
              {{ $t('common.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
            @update:density="density = $event" @refresh="getList">
            <template #left>
              <span class="ds-panel__title">{{ roomName || $t('oweFeeCallable.list.title') }}</span>
            </template>
          </ds-toolbar>

          <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
            :density="density" border :empty-description="$t('oweFeeCallable.page.empty')" @retry="getList">
            <el-table-column prop="ofcId" :label="$t('oweFeeCallable.table.id')" align="center" width="120">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.ofcId || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="ownerName" :label="$t('oweFeeCallable.table.ownerName')" align="center" width="110">
              <template slot-scope="scope">
                {{ scope.row.ownerName || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="payerObjName" :label="$t('oweFeeCallable.table.payerObjName')" align="left"
              min-width="140" show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.payerObjName || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="feeName" :label="$t('oweFeeCallable.table.feeName')" align="left" min-width="130"
              show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.feeName || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="amountdOwed" :label="$t('oweFeeCallable.table.amountdOwed')" align="right" width="130">
              <template slot-scope="scope">
                <span class="ds-num">{{ formatMoney(scope.row.amountdOwed) }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('oweFeeCallable.table.timeRange')" align="center" width="300">
              <template slot-scope="scope">
                <span class="ds-num">{{ formatDateTimeSec(scope.row.startTime) }} ~ {{ formatDateTimeSec(scope.row.endTime) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="callableWayName" :label="$t('oweFeeCallable.table.callableWayName')" align="center"
              width="120">
              <template slot-scope="scope">
                {{ scope.row.callableWayName || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="staffName" :label="$t('oweFeeCallable.table.staffName')" align="center" width="110">
              <template slot-scope="scope">
                {{ scope.row.staffName || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="stateName" :label="$t('oweFeeCallable.table.stateName')" align="center" width="110">
              <template slot-scope="scope">
                {{ scope.row.stateName || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="remark" :label="$t('oweFeeCallable.table.remark')" align="left" min-width="160"
              show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.remark || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="createTime" :label="$t('oweFeeCallable.table.createTime')" align="center" width="170">
              <template slot-scope="scope">
                {{ formatDateTimeSec(scope.row.createTime) }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('common.operation')" align="center" width="100">
              <template slot-scope="scope">
                <el-button type="text" size="small" class="ds-btn-danger" @click="handleDelete(scope.row)">
                  {{ $t('common.delete') }}
                </el-button>
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
            :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handlePageChange" />
        </div>
      </div>
    </div>

    <write-owe-fee-callable ref="writeOweFeeCallable" @success="handleSuccess"></write-owe-fee-callable>
    <delete-owe-fee-callable ref="deleteOweFeeCallable" @success="handleSuccess"></delete-owe-fee-callable>
  </div>
</template>

<script>
import { listOweFeeCallable } from '@/api/fee/oweFeeCallableApi'
import { getCommunityId } from '@/api/community/communityApi'
import RoomTreeDiv from '@/components/room/roomTreeDiv'
import WriteOweFeeCallable from '@/components/fee/writeOweFeeCallable'
import DeleteOweFeeCallable from '@/components/fee/deleteOweFeeCallable'
import { formatDateTimeSec, formatMoney } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'OweFeeCallableList',
  components: {
    RoomTreeDiv,
    WriteOweFeeCallable,
    DeleteOweFeeCallable,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      roomName: '',
      roomId: '',
      searchForm: {
        ownerName: '',
        feeName: '',
        callableWay: '',
        staffName: '',
        state: '',
        payerObjId: ''
      },
      tableData: [],
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.getList()
  },
  methods: {
    async getList() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          page: this.pagination.current,
          row: this.pagination.size,
          communityId: this.communityId,
          ...this.searchForm
        }
        const { data, total } = await listOweFeeCallable(params)
        this.tableData = Array.isArray(data) ? data : []
        this.pagination.total = total || 0
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('oweFeeCallable.page.loadFailed')
        this.$message.error(this.$t('oweFeeCallable.fetchError'))
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pagination.current = 1
      this.getList()
    },
    handleReset() {
      this.searchForm = {
        ownerName: '',
        feeName: '',
        callableWay: '',
        staffName: '',
        state: '',
        payerObjId: this.searchForm.payerObjId
      }
      this.handleSearch()
    },
    handleSelectRoom({ roomId, roomName }) {
      this.roomId = roomId
      this.roomName = roomName
      this.searchForm.payerObjId = roomId
      this.handleSearch()
    },
    handleWritePrint() {
      if (!this.roomId) {
        this.$message.warning(this.$t('oweFeeCallable.selectRoomFirst'))
        return
      }
      this.$refs.writeOweFeeCallable.open({
        roomId: this.roomId,
        roomName: this.roomName
      })
    },
    handleAdd() {
      this.$router.push('/views/fee/addOweFeeCallable')
    },
    handleDelete(row) {
      this.$refs.deleteOweFeeCallable.open(row)
    },
    handleSuccess() {
      this.getList()
    },
    handleSizeChange(val) {
      this.pagination.size = val
      this.getList()
    },
    handlePageChange(val) {
      this.pagination.current = val
      this.getList()
    },
    formatDateTimeSec(value) {
      return formatDateTimeSec(value)
    },
    formatMoney(value) {
      return formatMoney(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.owe-fee-callable-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 左侧房屋树 + 右侧主内容 */
.owe-fee-callable-layout {
  display: flex;
  gap: var(--ds-spacing-md);
  align-items: flex-start;
}

.owe-fee-callable-main {
  flex: 1 1 auto;
  min-width: 0;
}

/* 房屋树面板 */
.ds-panel--tree {
  width: 280px;
  flex-shrink: 0;
  overflow: auto;
  max-height: calc(100vh - 220px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}
</style>
